import React from "react";
import DOMPurify from "dompurify";
import { Icon } from "../components/Icon";
import { Num } from "../components/Num";
import { hasVisibleContent } from "../utils";
import { orderSelectedIds } from "../pickerGrouping";
import type {
  AgendaItem,
  AgendaSection,
  DroppedAgendaItem,
  FormData,
  Stance
} from "../types";

export interface DoneProps {
  form: FormData;
  selections: Record<number, Stance>;
  agendaItems: AgendaItem[];
  sections: AgendaSection[];
  hasPickStep: boolean;
  onRestart: () => void;
  droppedItems: DroppedAgendaItem[];
  confirmationContent?: string;
}

export const Done: React.FC<DoneProps> = ({
  form,
  selections,
  agendaItems,
  sections,
  hasPickStep,
  onRestart,
  droppedItems,
  confirmationContent
}) => {
  const ids = orderSelectedIds(selections, agendaItems, sections);
  const count = ids.length;
  // AB#10773: when every selected speaker item was dropped (Active / closed
  // registration), attendance was still saved but the clerk queue was not.
  // Do not show speaker-success chrome in that case.
  const allSpeakerSelectionsDropped =
    hasPickStep && droppedItems.length > 0 && count === 0;
  return (
    <div className="confirmation">
      <div className="check">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12l5 5L20 7" />
        </svg>
      </div>
      {/* AB#9443: the name is PII — keep it wrapped in notranslate so Google
          Translate leaves it alone. AB#9707: the comma AND the terminal period
          live INSIDE that same untranslated island. When they sat in bare text
          nodes around the span, Google translated the sentence as a whole and
          redistributed it across those fragments non-deterministically — shoving
          the period ahead of the name (Spanish rendered ".Jay Dawkins."). With
          one translatable text node ("You're checked in") plus one opaque island
          there is nothing left to shuffle. */}
      <h1>
        {form.first ? (
          <>
            You're checked in
            <span className="notranslate">, {form.first}.</span>
          </>
        ) : (
          "You're checked in."
        )}
      </h1>
      {hasVisibleContent(confirmationContent) ? (
        <div
          className="kiosk-confirmation-content"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(confirmationContent as string)
          }}
        />
      ) : (
        <p>
          {allSpeakerSelectionsDropped
            ? "Your attendance has been saved. Speaker sign-up closed before submission, so you were not added to the speaker list."
            : count > 0
              ? `Your registration has been received and saved. You'll be called up to speak on your ${count} item${
                  count === 1 ? "" : "s"
                } when ${count === 1 ? "it's" : "they're"} reached.`
              : "Your registration has been received and saved."}
        </p>
      )}

      {droppedItems.length > 0 && (
        <div className="dropped-notice" role="status" aria-live="polite">
          <div className="dropped-head">
            <Icon name="help" size={20} />
            <strong>
              {droppedItems.length === 1
                ? "1 item closed for sign-up before submission"
                : `${droppedItems.length} items closed for sign-up before submission`}
            </strong>
          </div>
          <p>
            {allSpeakerSelectionsDropped
              ? "Attendance was saved. See the clerk if you still need to be added to these items."
              : "Your other selections were saved. See the clerk to be added to the closed items."}
          </p>
          <ul>
            {droppedItems.map((d) => (
              <li key={d.id}>
                <strong>
                  <Num>#{(d.itemNumber || "").replace(/\.\s*$/, "") || d.id}</Num>
                </strong>{" "}
                {d.title}
              </li>
            ))}
          </ul>
        </div>
      )}
      {hasPickStep && !allSpeakerSelectionsDropped && (
        <dl className="speaking-summary">
          <dt>Speaking on</dt>
          <dd>
            <Num className="count">{count}</Num>
            <span className="unit">item{count === 1 ? "" : "s"}</span>
          </dd>
        </dl>
      )}
      <button type="button" className="btn lg primary" onClick={onRestart}>
        Done — next person <Icon name="arrow-right" size={22} />
      </button>
    </div>
  );
};
